<template>
    <div>
        <p class="mb-xl">Please fill in the customer details below.</p>
        <div class="fields">
            <FieldInput class="field--full" :value="name" label="Name" placeholder="Type here" :required="true" :v="$v.name" @input="updateField({ field: 'name', value: $event })" />
            <FieldPlaces class="field--full" :value="address" label="Address" placeholder="Type here" :required="true" :v="$v.address" @input="updateField({ field: 'address', value: $event })" />
            <FieldSelect :value="buildingType" :options="['Domestic', 'Non-domestic']" label="Building Type" placeholder="Select one" :required="true" :v="$v.buildingType" tooltip="Building classification as specified in the National Construction Code (NCC)" @input="updateField({ field: 'buildingType', value: $event })" />
            <FieldInput :value="nmi" label="NMI" placeholder="Type here" @input="updateField({ field: 'nmi', value: $event })" />
            <FieldInput :value="meterNumber" label="Meter Number" placeholder="Type here" @input="updateField({ field: 'meterNumber', value: $event })" />
            <FieldSelect :value="noOfPhases" :options="[1,2,3]" label="Number of phases" placeholder="Type here" tooltip="Number of phases - electrical installation. Selection determines products available in the job" :required="true" :v="$v.noOfPhases" @input="updateField({ field: 'noOfPhases', value: $event })" />
            <FieldInput type="email" class="field--full" :value="email" label="Email" placeholder="Type here" :required="true" :v="$v.email" @input="updateField({ field: 'email', value: $event })" />
            <FieldInput type="tel" :value="phone" label="Phone" placeholder="Type here" @input="updateField({ field: 'phone', value: $event })" />
            <FieldInput :value="jobNumber" label="Job Number" placeholder="Type here" @input="updateField({ field: 'jobNumber', value: $event })" />
            <FieldTextarea class="field--full" :value="notes" label="Notes" placeholder="Type here" :max-length="200" @input="updateField({ field: 'notes', value: $event })" />
            <FieldDate :value="installationDate" label="Proposed date of installation" placeholder="DD/MM/YYYY" @input="updateField({ field: 'installationDate', value: $event })" />
            <FieldCheckboxes class="field--full" :value="consent" :options="[{ label: 'By ticking this box, you consent and agree that you are submitting the information on this form under the terms of our <a class=\'link\' href=\'/legal\' target=\'_blank\'>Website User Terms and Conditions</a>. You also confirm that you have read and acknowledged the <a class=\'link\' href=\'/industry/installers/compliance-toolkit/accreditation-terms-and-conditions\' target=\'_blank\'>Accreditation Terms and Conditions</a>', value: 'Confirmed' }]" :v="$v.consent" @input="updateField({ field: 'consent', value: $event })" />
        </div>

        <div class="fields my-xl">
            <div class="field--full">
                <p class="mb-md">Please confirm the details of the accredited designer and installer. If you are not responsible for both the design and installation you will need to enter the name and accreditation number of the accredited person you are working with.</p>
                <p class="h4">{{ role }} details</p>
            </div>

            <FieldSelect class="field--full" :value="role" :options="['Designer and installer', 'Designer', 'Installer']" label="Role type" @input="setRole($event, updateField)" />

            <div>
                <p class="h6 mb-sm">Name</p>
                <p>{{ user.name }}</p>
            </div>

            <div>
                <p class="h6 mb-sm">Accreditation Number</p>
                <p>{{ user.accreditationNo }}</p>
            </div>

            <template v-if="role != 'Designer and Installer'">
                <p class="h4 field--full mt-md">{{ additionalRole }} Details</p>
                <FieldInput :value="additionalName" label="Name" placeholder="Type here" :required="true" :v="$v.additionalName" @input="updateField({ field: 'additionalName', value: $event })" />
                <FieldInput :value="additionalNumber" label="Accreditation Number" placeholder="Type here" :required="true" @input="updateField({ field: 'additionalNumber', value: $event })" />
            </template>
        </div>

        <WarningPrompt
            v-if="role == 'Designer'"
            small
            text="Commissioning sheet is not available for jobs where a third party installer is designated."
        />

        <div class="fields mt-lg">
            <p class="field--full">You may upload a company logo to be displayed on the documentation for this job.</p>
            <p class="h4 field--full logo-heading">
                Company logo
                <span class="small text--grey-light">10MB max</span>
            </p>

            <div class="field fileupload-outline field--full">
                <div class="fileupload-outline__uploader">
                    <label class="fileupload-outline__label" for="file-upload">
                        <svg class="icon ">
                            <use xlink:href="#paperclip" />
                        </svg>
                        <span class="fileupload-outline__status">{{ logoName || 'Choose file - Accepted files JPEG, PJG, PNG' }}</span>
                        <span class="fileupload-outline__size" />
                    </label>
                    <!-- <div class="fileupload-outline__button btn btn--ghost">Upload</div> -->
                    <input id="file-upload" type="file" name="file-upload" @change="convertFile">
                </div>

                <button class="logo-remove" @click="removeFile">
                    <svg class="icon"><use xlink:href="#remove" /></svg>
                    <span class="small semi text--orange">Remove file</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, email, requiredIf } from 'vuelidate/lib/validators'
import FieldInput from '@/form/Input';
import FieldPlaces from '@/form/Places';
import FieldSelect from '@/form/Select';
import FieldTextarea from '@/form/Textarea';
import FieldDate from '@/form/Date';
import FieldCheckboxes from '@/form/Checkboxes';
import WarningPrompt from '@/components/WarningPrompt';

export default {
    components: {
        FieldInput,
        FieldPlaces,
        FieldSelect,
        FieldTextarea,
        FieldDate,
        FieldCheckboxes,
        WarningPrompt,
    },

    mixins: [validationMixin],

    validations: {
        name: {
            required,
        },
        address: {
            string: {
                required
            },
            lat: {
                required
            },
            lng: {
                required
            },
        },
        buildingType: {
            required,
        },
        noOfPhases: {
            required,
        },
        email: {
            required,
            email,
        },
        consent: {
            required,
        },
        additionalName: {
            required: requiredIf((model) => {
                return model.additionalRole === 'Designer' || model.additionalRole === 'Installer';
            })
        }
    },

    data() {
        return {
            user: JSON.parse(this.$root.user)
        }
    },

    computed: mapState('job', [
        'name',
        'address',
        'buildingType',
        'nmi',
        'meterNumber',
        'noOfPhases',
        'email',
        'phone',
        'jobNumber',
        'notes',
        'installationDate',
        'consent',
        'logoName',
        'role',
        'additionalRole',
        'additionalName',
        'additionalNumber'
    ]),

    created() {
        this.fetchProducts();
    },

    methods: {
        ...mapActions('job', ['updateField']),
        ...mapActions('products', ['fetchProducts']),

        setRole(role, set) {
            if (role == 'Designer and Installer') {

                set({ field: 'role', value: role });
                set({ field: 'additionalRole', value: undefined });
                set({ field: 'additionalName', value: undefined });
                set({ field: 'additionalNumber', value: undefined });
            }

            if (role == 'Designer') {

                set({ field: 'role', value: role });
                set({ field: 'additionalRole', value: 'Installer' });
            }

            if (role == 'Installer') {

                set({ field: 'role', value: role });
                set({ field: 'additionalRole', value: 'Designer' });
            }
        },

        toBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        async convertFile(event) {
            if (event.target.files.length) {
                const file = event.target.files[0];
                const encoded = await this.toBase64(file);
                this.updateField({ field: 'logoName', value: file.name });
                this.updateField({ field: 'logoEncoded', value: encoded });
            } else {
                this.updateField({ field: 'logoName', value: undefined });
                this.updateField({ field: 'logoEncoded', value: undefined });
            }
        },

        removeFile() {
            this.updateField({ field: 'logoName', value: undefined });
            this.updateField({ field: 'logoEncoded', value: undefined });
        }
    }
};
</script>

<style lang="scss" scoped>
    ::v-deep .tool-tip {
        right: -25px;
    }

    ::v-deep .tool-tip::after {
        right: 7%;
    }

    .logo-heading {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
    }

    .logo-remove {
        display: flex;
        align-items: center;
        margin-top: 10px;
        margin-left: auto;

        svg {
            margin-right: 5px;
        }
    }
</style>
